import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, User, Mail, Phone, Building, MapPin, Calendar,
  CreditCard, FileText, Receipt, Clock, CheckCircle, XCircle, Download,
  Edit, Save, X, Trash2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

export default function AdminClientDetail() {
  const { clientId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Edit billing state
  const [editingBilling, setEditingBilling] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingData, setBillingData] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    siret: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'France',
    phone: ''
  });
  
  // Edit email state
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  
  // Delete client state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchClientData = useCallback(async () => {
    try {
      // Fetch client details
      const clientRes = await fetch(`${API_URL}/api/admin/clients/${clientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!clientRes.ok) {
        navigate('/admin/clients');
        return;
      }
      const clientData = await clientRes.json();
      setClient(clientData);

      // Fetch all related data
      const [subsRes, quotesRes, invoicesRes, txRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/clients/${clientId}/subscriptions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/clients/${clientId}/quotes`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/clients/${clientId}/invoices`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/clients/${clientId}/transactions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (subsRes.ok) setSubscriptions(await subsRes.json());
      if (quotesRes.ok) setQuotes(await quotesRes.json());
      if (invoicesRes.ok) setInvoices(await invoicesRes.json());
      if (txRes.ok) setTransactions(await txRes.json());
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, clientId, token, navigate]);

  useEffect(() => {
    fetchClientData();
  }, [fetchClientData]);

  const startEditingBilling = () => {
    const billing = client?.billing_info || {};
    setBillingData({
      first_name: billing.first_name || '',
      last_name: billing.last_name || '',
      company_name: billing.company_name || '',
      siret: billing.siret || '',
      address: billing.address || '',
      city: billing.city || '',
      postal_code: billing.postal_code || '',
      country: billing.country || 'France',
      phone: billing.phone || ''
    });
    setEditingBilling(true);
  };

  const handleBillingChange = (e) => {
    setBillingData({ ...billingData, [e.target.name]: e.target.value });
  };

  const saveBillingInfo = async () => {
    setBillingLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/clients/${clientId}/billing`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(billingData)
      });
      if (res.ok) {
        await fetchClientData();
        setEditingBilling(false);
        toast.success('Informations de facturation mises à jour');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setBillingLoading(false);
    }
  };

  const startEditingEmail = () => {
    setNewEmail(client?.email || '');
    setEditingEmail(true);
  };

  const saveEmail = async () => {
    if (!newEmail.trim() || newEmail === client?.email) {
      setEditingEmail(false);
      return;
    }
    
    setEmailLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/clients/${clientId}/email`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: newEmail })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        await fetchClientData();
        setEditingEmail(false);
        toast.success(data.message || 'Email mis à jour');
      } else {
        toast.error(data.detail || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleDeleteClient = async () => {
    setDeleteLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/clients/${clientId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Client supprimé avec succès');
        navigate('/admin/clients');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleCancelSubscription = async (subId) => {
    if (!window.confirm('Annuler cet abonnement ?')) return;
    setActionLoading(subId);
    try {
      const res = await fetch(`${API_URL}/api/admin/subscriptions/${subId}/cancel`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        await fetchClientData();
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirmCancellation = async (subId) => {
    if (!window.confirm('Confirmer l\'annulation ? L\'abonnement sera annulé sans remboursement.')) return;
    setActionLoading(subId);
    try {
      const res = await fetch(`${API_URL}/api/admin/subscriptions/${subId}/confirm-cancellation`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        await fetchClientData();
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectCancellation = async (subId) => {
    if (!window.confirm('Rejeter la demande ? L\'abonnement restera actif.')) return;
    setActionLoading(subId);
    try {
      const res = await fetch(`${API_URL}/api/admin/subscriptions/${subId}/reject-cancellation`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        await fetchClientData();
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const downloadPDF = async (invoiceId, invoiceNumber) => {
    setDownloading(invoiceId);
    try {
      const res = await fetch(`${API_URL}/api/admin/invoices/${invoiceId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Facture_${invoiceNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
    } finally {
      setDownloading(null);
    }
  };

  const StatusBadge = ({ status, type = 'default' }) => {
    const styles = {
      active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      paid: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      cancellation_requested: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      Expiré: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
      failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
    };
    const labels = {
      active: 'Actif', paid: 'Payé', pending: 'En attente', 
      cancellation_requested: 'Annulation demandée',
      cancelled: 'Annulé', Expiré: 'Expiré', failed: 'Échoué'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-muted rounded animate-pulse" />
        <div className="h-64 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  if (!client) {
    return <div>Client non trouvé</div>;
  }

  const billing = client.billing_info || {};

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/admin/clients">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold">
              {billing.first_name || billing.last_name 
                ? `${billing.first_name} ${billing.last_name}`.trim()
                : client.email
              }
            </h1>
            <p className="text-muted-foreground">{client.email}</p>
          </div>
        </div>
        <Button 
          variant="destructive" 
          size="sm"
          onClick={() => setShowDeleteConfirm(true)}
          data-testid="delete-client-btn"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Supprimer le client
        </Button>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="delete-client-modal">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="text-destructive flex items-center gap-2">
                <Trash2 className="h-5 w-5" />
                Supprimer ce client ?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Cette action est <strong>irréversible</strong>. Toutes les données associées seront supprimées :
              </p>
              <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
                <li>Abonnements (annulés sur Stripe)</li>
                <li>Devis</li>
                <li>Factures</li>
                <li>Historique de paiements</li>
              </ul>
              <div className="flex gap-3 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleteLoading}
                  data-testid="cancel-delete-btn"
                >
                  Annuler
                </Button>
                <Button 
                  variant="destructive" 
                  className="flex-1"
                  onClick={handleDeleteClient}
                  disabled={deleteLoading}
                  data-testid="confirm-delete-btn"
                >
                  {deleteLoading ? 'Suppression...' : 'Confirmer la suppression'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Client Info Card */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Informations client
              </div>
              {!editingBilling ? (
                <Button variant="ghost" size="sm" onClick={startEditingBilling}>
                  <Edit className="h-4 w-4" />
                </Button>
              ) : (
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setEditingBilling(false)} disabled={billingLoading}>
                    <X className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={saveBillingInfo} disabled={billingLoading} className="text-green-600">
                    <Save className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {editingBilling ? (
              /* Edit Mode */
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-muted-foreground">Prénom *</label>
                    <Input name="first_name" value={billingData.first_name} onChange={handleBillingChange} placeholder="Jean" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Nom *</label>
                    <Input name="last_name" value={billingData.last_name} onChange={handleBillingChange} placeholder="Dupont" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Téléphone *</label>
                  <Input name="phone" value={billingData.phone} onChange={handleBillingChange} placeholder="06 12 34 56 78" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Adresse *</label>
                  <Input name="address" value={billingData.address} onChange={handleBillingChange} placeholder="123 rue..." />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-muted-foreground">CP *</label>
                    <Input name="postal_code" value={billingData.postal_code} onChange={handleBillingChange} placeholder="75001" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Ville *</label>
                    <Input name="city" value={billingData.city} onChange={handleBillingChange} placeholder="Paris" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Pays</label>
                  <Input name="country" value={billingData.country} onChange={handleBillingChange} placeholder="France" />
                </div>
                <div className="pt-2 border-t">
                  <p className="text-xs text-muted-foreground mb-2">Entreprise (optionnel)</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Input name="company_name" value={billingData.company_name} onChange={handleBillingChange} placeholder="Société" />
                    <Input name="siret" value={billingData.siret} onChange={handleBillingChange} placeholder="SIREN" />
                  </div>
                </div>
              </div>
            ) : (
              /* View Mode */
              <>
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  {editingEmail ? (
                    <div className="flex items-center gap-2 flex-1">
                      <Input 
                        type="email"
                        value={newEmail} 
                        onChange={(e) => setNewEmail(e.target.value)}
                        className="h-8 text-sm"
                        placeholder="nouvel@email.com"
                      />
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={saveEmail}
                        disabled={emailLoading}
                        className="h-8 w-8 p-0"
                      >
                        <Save className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setEditingEmail(false)}
                        disabled={emailLoading}
                        className="h-8 w-8 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 flex-1">
                      <span className="text-sm">{client.email}</span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={startEditingEmail}
                        className="h-6 w-6 p-0 opacity-50 hover:opacity-100"
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
                
                {/* Name */}
                {(billing.first_name || billing.last_name) ? (
                  <div className="flex items-center gap-3">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{billing.first_name} {billing.last_name}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 text-orange-600">
                    <User className="h-4 w-4" />
                    <span className="text-sm italic">Nom non renseigné</span>
                  </div>
                )}
                
                {/* Phone */}
                {billing.phone ? (
                  <div className="flex items-center gap-3">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <a href={`tel:${billing.phone}`} className="text-sm hover:text-primary">{billing.phone}</a>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 text-orange-600">
                    <Phone className="h-4 w-4" />
                    <span className="text-sm italic">Téléphone non renseigné</span>
                  </div>
                )}
                
                {/* Company (optional) */}
                {billing.company_name && (
                  <div className="flex items-center gap-3">
                    <Building className="h-4 w-4 text-muted-foreground" />
                    <div className="text-sm">
                      <p className="font-medium">{billing.company_name}</p>
                      {billing.siret && <p className="text-muted-foreground text-xs">SIREN: {billing.siret}</p>}
                    </div>
                  </div>
                )}
                
                {/* Address */}
                {billing.address ? (
                  <div className="flex items-start gap-3">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <div className="text-sm">
                      <p>{billing.address}</p>
                      <p>{billing.postal_code} {billing.city}</p>
                      <p className="text-muted-foreground">{billing.country || 'France'}</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 text-orange-600">
                    <MapPin className="h-4 w-4" />
                    <span className="text-sm italic">Adresse non renseignée</span>
                  </div>
                )}
                
                {/* Warning if profile incomplete */}
                {(!billing.first_name || !billing.last_name || !billing.phone || !billing.address) && (
                  <div className="mt-4 p-3 bg-orange-100 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                    <p className="text-sm text-orange-700 dark:text-orange-400 font-medium">
                      Profil incomplet
                    </p>
                    <p className="text-xs text-orange-600 dark:text-orange-500 mt-1">
                      Le client devra compléter ses informations à sa prochaine connexion.
                    </p>
                  </div>
                )}
                
                <div className="flex items-center gap-3 pt-4 border-t border-border">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Client depuis le {new Date(client.created_at).toLocaleDateString('fr-FR')}
                  </span>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="lg:col-span-2 grid sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Abonnement</p>
                  <p className="text-2xl font-bold">
                    {subscriptions.find(s => s.status === 'active') ? 'Actif' : 'Aucun'}
                  </p>
                </div>
                <CreditCard className={`h-8 w-8 ${subscriptions.find(s => s.status === 'active') ? 'text-green-500' : 'text-muted-foreground'}`} />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Devis</p>
                  <p className="text-2xl font-bold">{quotes.length}</p>
                  <p className="text-xs text-muted-foreground">
                    {quotes.filter(q => q.status === 'pending').length} en attente
                  </p>
                </div>
                <FileText className="h-8 w-8 text-primary" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total payé</p>
                  <p className="text-2xl font-bold">
                    {transactions.filter(t => t.payment_status === 'paid').reduce((sum, t) => sum + t.amount, 0)}€
                  </p>
                </div>
                <Receipt className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Subscriptions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Abonnements
          </CardTitle>
        </CardHeader>
        <CardContent>
          {subscriptions.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">Aucun abonnement</p>
          ) : (
            <div className="space-y-4">
              {subscriptions.map((sub) => (
                <div key={sub.id} className="flex flex-col md:flex-row md:items-center justify-between p-4 border border-border rounded-xl gap-4">
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${sub.status === 'active' ? 'bg-green-100 dark:bg-green-900/30' : sub.status === 'cancellation_requested' ? 'bg-orange-100 dark:bg-orange-900/30' : 'bg-muted'}`}>
                      {sub.status === 'active' ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : sub.status === 'cancellation_requested' ? (
                        <Clock className="h-5 w-5 text-orange-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">
                        {sub.plan === 'hosting_resend' ? 'Hébergement + Emails' : 'Hébergement Annuel'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {sub.current_period_start && sub.current_period_end && (
                          <>
                            Du {new Date(sub.current_period_start).toLocaleDateString('fr-FR')} au {new Date(sub.current_period_end).toLocaleDateString('fr-FR')}
                          </>
                        )}
                      </p>
                      {sub.status === 'cancellation_requested' && sub.cancellation_reason && (
                        <p className="text-sm mt-2 p-2 bg-orange-50 dark:bg-orange-900/20 rounded text-orange-700 dark:text-orange-400">
                          <span className="font-medium">Raison:</span> {sub.cancellation_reason}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 md:gap-4">
                    <span className="font-semibold">{sub.amount}€/mois</span>
                    <StatusBadge status={sub.status} />
                    {sub.status === 'active' && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleCancelSubscription(sub.id)}
                        disabled={actionLoading === sub.id}
                        className="text-destructive hover:text-destructive"
                      >
                        Annuler
                      </Button>
                    )}
                    {sub.status === 'cancellation_requested' && (
                      <>
                        <Button 
                          variant="outline"
                          size="sm"
                          onClick={() => handleRejectCancellation(sub.id)}
                          disabled={actionLoading === sub.id}
                          className="text-green-600 border-green-600 hover:bg-green-50"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Rejeter
                        </Button>
                        <Button 
                          variant="destructive"
                          size="sm"
                          onClick={() => handleConfirmCancellation(sub.id)}
                          disabled={actionLoading === sub.id}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Confirmer
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quotes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Devis
          </CardTitle>
        </CardHeader>
        <CardContent>
          {quotes.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">Aucun devis</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-border">
                  <tr>
                    <th className="text-left p-3 font-medium text-muted-foreground">Titre</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Date</th>
                    <th className="text-right p-3 font-medium text-muted-foreground">Montant</th>
                    <th className="text-center p-3 font-medium text-muted-foreground">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.map((quote) => (
                    <tr key={quote.id} className="border-b border-border last:border-0">
                      <td className="p-3">
                        <p className="font-medium">{quote.title}</p>
                        {quote.description && (
                          <p className="text-sm text-muted-foreground truncate max-w-xs">{quote.description}</p>
                        )}
                      </td>
                      <td className="p-3 text-muted-foreground">
                        {new Date(quote.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="p-3 text-right font-semibold">{quote.amount}€</td>
                      <td className="p-3 text-center">
                        <StatusBadge status={quote.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoices */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Factures
          </CardTitle>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">Aucune facture</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-border">
                  <tr>
                    <th className="text-left p-3 font-medium text-muted-foreground">N° Facture</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Date</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Type</th>
                    <th className="text-right p-3 font-medium text-muted-foreground">Montant</th>
                    <th className="text-right p-3 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id} className="border-b border-border last:border-0">
                      <td className="p-3 font-medium">{invoice.invoice_number}</td>
                      <td className="p-3 text-muted-foreground">
                        {new Date(invoice.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          invoice.type === 'subscription' 
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            : invoice.type === 'manual'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                        }`}>
                          {invoice.type === 'subscription' ? 'Abonnement' : invoice.type === 'manual' ? 'Manuel' : 'Devis'}
                        </span>
                      </td>
                      <td className="p-3 text-right font-semibold">{invoice.amount}€</td>
                      <td className="p-3 text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => downloadPDF(invoice.id, invoice.invoice_number)}
                          disabled={downloading === invoice.id}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          {downloading === invoice.id ? '...' : 'PDF'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Historique des paiements
          </CardTitle>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">Aucun paiement</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-border">
                  <tr>
                    <th className="text-left p-3 font-medium text-muted-foreground">Date</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Type</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Méthode</th>
                    <th className="text-right p-3 font-medium text-muted-foreground">Montant</th>
                    <th className="text-center p-3 font-medium text-muted-foreground">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => (
                    <tr key={tx.id} className="border-b border-border last:border-0">
                      <td className="p-3 text-muted-foreground">
                        {new Date(tx.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="p-3">
                        {tx.type === 'subscription' ? 'Abonnement' : 'Devis'}
                      </td>
                      <td className="p-3 text-muted-foreground">
                        {tx.payment_method === 'klarna' ? 'Klarna' : 'Carte'}
                      </td>
                      <td className="p-3 text-right font-semibold">{tx.amount}€</td>
                      <td className="p-3 text-center">
                        <StatusBadge status={tx.payment_status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
